/**
 * Narrator box (implements NarratorApi): The Scientist's portrait with mood expressions,
 * typewriter subtitles, voice babble via audio.voice(), priority handling.
 */
import type { AudioApi, NarratorApi, NarratorMood } from '@core/services';
import { el, escapeHtml } from '../dom';
import { PORTRAIT_SVG } from './portrait';

const MOOD_LABEL: Record<NarratorMood, string> = {
  snark: 'unimpressed',
  alarm: 'alarmed',
  proud: 'proud',
  bored: 'bored',
  excited: 'excited',
};

export class Narrator implements NarratorApi {
  readonly root = el('div', 'narrator glass');
  private portrait = el('div', 'portrait mood-snark', PORTRAIT_SVG);
  private who = el('div', 'who', 'The Scientist<span class="mood"></span>');
  private line = el('div', 'line');
  private current = { priority: -1, until: 0 };
  private lastPick = new Map<string, number>();
  private timer?: ReturnType<typeof setTimeout>;
  private typeTimer?: ReturnType<typeof setInterval>;
  private resolveCurrent?: () => void;
  subtitles = true;
  /** Seconds-per-character multiplier (reduced motion → instant text). */
  instant = false;
  /** Fired with (text, mood) whenever a line starts (for logs/QA). */
  onLine?: (text: string, mood: NarratorMood) => void;

  constructor(private readonly audio: AudioApi) {
    const txt = el('div', 'txt');
    txt.append(this.who, this.line);
    this.root.append(this.portrait, txt);
  }

  setCinematic(on: boolean): void {
    this.root.classList.toggle('cinematic', on);
  }

  say(text: string, opts: { mood?: NarratorMood; priority?: number; duration?: number } = {}): Promise<void> {
    const priority = opts.priority ?? 1;
    const mood = opts.mood ?? 'snark';
    const now = performance.now();
    if (now < this.current.until && priority < this.current.priority) return Promise.resolve();
    this.finish(false);
    const duration = opts.duration ?? Math.min(6000, 1500 + text.length * 45);
    this.current = { priority, until: now + duration };
    this.portrait.className = `portrait mood-${mood} talking`;
    (this.who.lastChild as HTMLElement).textContent = `· ${MOOD_LABEL[mood]}`;
    this.root.classList.toggle('visible', this.subtitles);
    this.audio.voice(text, { mood });
    this.onLine?.(text, mood);
    this.typewrite(text, Math.min(duration * 0.45, text.length * 18));
    return new Promise((resolve) => {
      this.resolveCurrent = resolve;
      this.timer = setTimeout(() => this.finish(true), duration);
    });
  }

  private typewrite(text: string, totalMs: number): void {
    clearInterval(this.typeTimer);
    const html = (n: number) => {
      const shown = escapeHtml(text.slice(0, n)).replace(/\*burp\*/gi, '<em>*burp*</em>');
      return n < text.length ? `${shown}<span class="cur"></span>` : shown;
    };
    if (this.instant || totalMs <= 0) {
      this.line.innerHTML = html(text.length);
      this.portrait.classList.remove('talking');
      return;
    }
    let n = 0;
    const step = Math.max(12, totalMs / Math.max(1, text.length));
    const per = Math.max(1, Math.round(16 / step));
    const burpAt = text.toLowerCase().indexOf('*burp*');
    this.line.innerHTML = html(0);
    this.typeTimer = setInterval(() => {
      n = Math.min(text.length, n + per);
      this.line.innerHTML = html(n);
      if (burpAt >= 0 && n >= burpAt && n - per < burpAt) {
        this.portrait.classList.remove('burping');
        void this.portrait.offsetWidth;
        this.portrait.classList.add('burping');
        this.audio.sfx('burp', { volume: 0.6 });
      }
      if (n >= text.length) {
        clearInterval(this.typeTimer);
        this.portrait.classList.remove('talking');
      }
    }, Math.max(16, step));
  }

  private finish(hide: boolean): void {
    clearTimeout(this.timer);
    clearInterval(this.typeTimer);
    if (hide) this.root.classList.remove('visible');
    this.current = { priority: -1, until: 0 };
    const r = this.resolveCurrent;
    this.resolveCurrent = undefined;
    r?.();
  }

  sayOneOf(lines: string[], opts?: { mood?: NarratorMood; priority?: number }): Promise<void> {
    if (!lines.length) return Promise.resolve();
    const key = lines[0];
    const prev = this.lastPick.get(key) ?? -1;
    let idx = Math.floor(Math.random() * lines.length);
    if (lines.length > 1 && idx === prev) idx = (idx + 1) % lines.length;
    this.lastPick.set(key, idx);
    return this.say(lines[idx], opts);
  }

  clear(): void {
    this.finish(true);
  }
}
